import { Box, LinearProgress, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";

interface ComprehensiveAnalysisProps {
  flags?: Record<string, any> | null;
  sentiment?: Record<string, any> | null;
  extractedFields?: Record<string, any> | null;
}

const formatLabel = (val: string) =>
  val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const isEmpty = (v: unknown): boolean =>
  v === null ||
  v === undefined ||
  v === "" ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" && v !== null && Object.keys(v).length === 0);

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.map((x) => formatValue(x)).join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  if (typeof v === "string") return formatLabel(v);
  return String(v);
};

const toPercent = (n: unknown): number | null => {
  const num = typeof n === "number" ? n : Number(n);
  if (!isFinite(num)) return null;
  return num <= 1 ? Math.round(num * 100) : Math.round(num);
};

const ComprehensiveAnalysis = ({
  flags,
  sentiment,
  extractedFields,
}: ComprehensiveAnalysisProps) => {
  const theme = useTheme();
  const p = theme.nawy.colors.primitives;

  const callType = flags?.call_type as string | undefined;
  const language = flags?.language as string | undefined;
  const clientDialect = flags?.client_dialect as string | undefined;
  const transcriptionQuality = flags?.transcription_quality as
    | string
    | undefined;
  const agentGender = flags?.agent_gender as string | undefined;
  const clientGender = flags?.client_gender as string | undefined;
  const agentTalkRatio = toPercent(flags?.agent_talk_ratio);
  const clientTalkRatio = toPercent(flags?.client_talk_ratio);
  const sentimentArc = sentiment?.arc as string | undefined;

  const detailRows = useMemo(() => {
    const rows: Array<{ label: string; value: string }> = [];
    if (callType)
      rows.push({ label: "Call type", value: formatLabel(callType) });
    if (language)
      rows.push({ label: "Language", value: formatLabel(language) });
    if (clientDialect)
      rows.push({ label: "Client dialect", value: formatLabel(clientDialect) });
    if (transcriptionQuality)
      rows.push({
        label: "Transcription quality",
        value: formatLabel(transcriptionQuality),
      });
    if (agentGender)
      rows.push({ label: "Agent gender", value: formatLabel(agentGender) });
    if (clientGender)
      rows.push({ label: "Client gender", value: formatLabel(clientGender) });
    if (sentimentArc)
      rows.push({ label: "Sentiment arc", value: sentimentArc.replace(/_/g, " ") });
    return rows;
  }, [
    callType,
    language,
    clientDialect,
    transcriptionQuality,
    agentGender,
    clientGender,
    sentimentArc,
  ]);

  const extractedEntries = useMemo(
    () =>
      Object.entries(extractedFields ?? {}).filter(
        ([, value]) => !isEmpty(value),
      ),
    [extractedFields],
  );

  const hasTalkRatio = agentTalkRatio !== null || clientTalkRatio !== null;
  const hasAnyContent =
    detailRows.length > 0 || hasTalkRatio || extractedEntries.length > 0;

  const sectionLabelSx = {
    fontSize: 10,
    fontWeight: 600,
    lineHeight: "16px",
    color: p.grey[600],
    textTransform: "uppercase" as const,
    letterSpacing: ".06em",
    mb: "8px",
  };

  const cardSx = {
    p: "12px",
    borderRadius: "12px",
    backgroundColor: theme.nawy.colors.semantic.layer.secondary,
  };

  if (!hasAnyContent) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: theme.nawy.spacing.xs,
          py: theme.nawy.spacing.xl,
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.sm.regular,
            color: theme.nawy.colors.semantic.text.tertiary,
          }}
        >
          No detailed analysis available for this call.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.sm,
      }}
    >
      {detailRows.length > 0 && (
        <Box sx={cardSx}>
          <Typography sx={sectionLabelSx}>Call details</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              columnGap: "16px",
              rowGap: "6px",
            }}
          >
            {detailRows.map((row) => (
              <Box key={row.label} sx={{ display: "contents" }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: p.grey[700],
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: p.navy[800],
                    textAlign: "left",
                  }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {hasTalkRatio && (
        <Box sx={cardSx}>
          <Typography sx={sectionLabelSx}>Talk ratio</Typography>
          {agentTalkRatio !== null && (
            <Box sx={{ mb: clientTalkRatio !== null ? "8px" : 0 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: "4px",
                }}
              >
                <Typography sx={{ fontSize: 12, color: p.grey[700] }}>
                  Agent
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: p.navy[800],
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {agentTalkRatio}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={agentTalkRatio}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: p.navy[50],
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: p.blue[500],
                  },
                }}
              />
            </Box>
          )}
          {clientTalkRatio !== null && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: "4px",
                }}
              >
                <Typography sx={{ fontSize: 12, color: p.grey[700] }}>
                  Client
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: p.navy[800],
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {clientTalkRatio}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={clientTalkRatio}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: p.navy[50],
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: p.violet[500],
                  },
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {extractedEntries.length > 0 && (
        <Box sx={cardSx}>
          <Typography sx={sectionLabelSx}>Extracted fields</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              columnGap: "16px",
              rowGap: "6px",
            }}
          >
            {extractedEntries.map(([key, value]) => (
              <Box key={key} sx={{ display: "contents" }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: p.grey[700],
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatLabel(key)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: p.navy[800],
                    textAlign: "right",
                    wordBreak: "break-word",
                  }}
                >
                  {formatValue(value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ComprehensiveAnalysis;
